<template>
  <section class="member-info-block">
    <FlexLayout col :gap="15">
      <!-- 會員資訊 -->
      <FlexLayout :gap="24">
        <div class="user-avatar">
          <img :src="memberAvatar" width="135" height="135">
        </div>
        <!-- <div class="user-container"> -->
        <FlexLayout col :gap="10" class="w-full">
          <!-- 會員統計資訊 -->

          <div class="flex items-center" style="gap: 20px">
            <p class="user-name">
              {{ displayMemberInfo.name }}
            </p>
            <p v-if="showControl.btn.inviteRecord" class="underline cursor-pointer text-primary-100 text-sm" @click="show.inviteRecord = true">{{ $t('member.memberInfoBlock.viewInviteRecord') }}</p>
          </div>

          <FlexLayout class="flex-1">
            <div class="user-statistic">
              <FlexLayout :gap="8" class="w-full flex-wrap">
                <FlexLayout v-if="showControl.field.memberLevel" col style="width: 240px;">
                  <p class="info-label flex items-center gap-2">
                    <span>{{ $t('member.memberInfoBlock.memberLevel') }}： {{ displayMemberInfo.level }}</span>
                    <BaseElButton
                      size="mini"
                      type="primary"
                      plain
                      @click="show.progressDrawer = true"
                    >
                      {{ $t('member.memberInfoBlock.levelProgressBar') }}
                    </BaseElButton>
                  </p>
                  <p class="info-label">
                    {{ $t('member.memberInfoBlock.expiryDate') }}：
                    {{ displayMemberInfo.levelExpiredDate }}
                  </p>
                </FlexLayout>
                <FlexLayout col style="width: 240px;">
                  <p class="info-label">
                    {{ $t('member.memberInfoBlock.phoneNumber') }}： {{ displayMemberInfo.phone }}
                  </p>
                  <p class="info-label">
                    {{ $t('member.memberInfoBlock.birthday') }}： {{ displayMemberInfo.birthday }}
                  </p>
                  <p class="info-label">
                    來源分店： {{ displayMemberInfo.sourceShop }}
                  </p>
                </FlexLayout>
              </FlexLayout>
              <FlexLayout :gap="8" class="w-full flex-wrap">
                <FlexLayout col style="width: 240px;">
                  <p class="flex items-center info-label">
                    {{ $t('member.memberInfoBlock.historicalTotalSpending') }}
                    <el-tooltip placement="right">
                      <div slot="content">
                        {{ $t('member.memberInfoBlock.historicalTotalSpendingTooltip') }}
                      </div>
                      <span
                        class="material-icons"
                        style="font-size: 16px; margin-left: 5px"
                      >
                        help_outline
                      </span>
                    </el-tooltip>
                  </p>
                  <p class="stat-value">$ {{ displayMemberInfo.totalPaymentPriceSum }}</p>
                </FlexLayout>
                <FlexLayout col style="width: 240px;">
                  <p class="info-label">{{ $t('member.memberInfoBlock.lineFriendName') }}</p>
                  <p class="stat-value">{{ displayMemberInfo.lineDisplayName }}</p>
                </FlexLayout>
                <FlexLayout v-if="showControl.field.cyjPosBinding" col class="w-[240px]">
                  <p class="info-label">
                    {{ $t('member.tab.memberInfo.cyjPosBinding') }}
                  </p>
                  <p class="stat-value">
                    <span v-if="cyjPosCsid">{{ cyjPosCsid }}</span>
                    <BaseElButton
                      v-else-if="showControl.btn.syncCyjPos"
                      size="mini"
                      type="primary"
                      plain
                      :loading="syncCyjPosLoading"
                      @click="syncCyjPosMember"
                    >
                      {{ $t('member.memberProfile.syncToPos') }}
                    </BaseElButton>
                    <span v-else>-</span>
                  </p>
                </FlexLayout>
              </FlexLayout>
            </div>
          </FlexLayout>
        </FlexLayout>
        <!-- </div> -->
      </FlexLayout>
      <!-- 會員標籤 -->
      <MemberTags
        :member="member"
        :mTagsList="tags"
        :customTagsList="customTags"
        @update:customTags="handleUpdateCustomTags"
      />
      <!-- 會員資訊 -->
      <BalanceStatistic
        :member="member"
        :reservationStatistic="reservationStatistic"
        :rebateWalletBalance="rebateWalletBalance"
        :usePospal="usePospal"
        :useExternalPoint="useExternalPoint"
        :pointWalletBalance="pointWalletBalance"
        :pospalWalletBalance="pospalWalletBalance"
        :externalPointWalletBalance="externalPointWalletBalance"
      />
    </FlexLayout>
    <MemberInviteRecord
      v-if="show.inviteRecord"
      :member="member"
      @close="show.inviteRecord = false"
    />
    <MemberLevelProgressDrawer
      v-if="show.progressDrawer && memberId"
      :member-id="memberId"
      @close="show.progressDrawer = false"
    />
  </section>
</template>

<script>
import { imgSrc } from '@/utils/helper'
import { computed, defineComponent, reactive, ref, onMounted } from 'vue'
import { t } from '@/plugins/i18n/i18n'
import { get } from 'lodash'
import BalanceStatistic from './components/BalanceStatistic.vue'
import MemberTags from './components/MemberTags.vue'
import { useMemberContext } from '@/use/useMemberContext'
import MemberInviteRecord from '@/views/Members/components/MemberInviteRecord/MemberInviteRecord.vue'
import MemberLevelProgressDrawer from '@/views/Members/components/MemberLevelProgressDrawer/MemberLevelProgressDrawer.vue'
import dayjs from '@/lib/dayjs'
import { useRoute } from 'vue-router/composables'
import { formatDate } from '@/utils/date'
import { usePermissions } from '@/use/permissions'
import { useShop } from '@/use/shop'
import { SyncCyjPosMemberImmediately } from '@/api/cyjPos'

export default defineComponent({
  name: 'MemberInfoBlock',
  components: {
    BalanceStatistic,
    MemberTags,
    MemberInviteRecord,
    MemberLevelProgressDrawer,
  },

  setup () {
    const route = useRoute()

    // ========================================
    // Inject (從父組件注入數據)
    // ========================================
    const {
      member,
      memberTopInfo,
      levelList,
      tags,
      customTags,
      reservationStatistic,
      totalPaymentPriceSum,
      rebateWalletBalance,
      pointWalletBalance,
      pospalWalletBalance,
      externalPointWalletBalance,
      usePospal,
      useExternalPoint,
      updateMemberTags,
      fetchMemberTopInfo,
    } = useMemberContext()
    const { checkAction } = usePermissions()
    const { shopId } = useShop()

    // ========================================
    // Local State
    // ========================================
    const show = reactive({
      inviteRecord: false,
      progressDrawer: false,
    })

    // ========================================
    // Computed Properties
    // ========================================
    const memberId = computed(() => member.value?.id || '')

    const showControl = computed(() => ({
      field: {
        cyjPosBinding: checkAction('planModule.cyjPos.enable'),
        // 店家方案沒有會員等級（GIMMATEK 不提供）時隱藏等級、效期與等級進度條（CNCY-10766）
        memberLevel: checkAction('admin.memberLevelConfig.page'),
      },
      btn: {
        syncCyjPos: checkAction('admin.shopCustomModule.syncCyjPosMemberImmediately'),
        // 推薦會員屬行銷模組
        inviteRecord: checkAction('admin.memberReferralRecord.find'),
      },
    }))
    const cyjPosCsid = computed(() => get(member.value, 'UserInfo.systemCustom.cyjPosCsid'))
    const syncCyjPosLoading = ref(false)

    const memberLevel = computed(() => {
      const curLevel = member.value?.level
      if (!curLevel) {
        return memberTopInfo.value?.memberConfig?.nullLevelName || t('member.memberInfoBlock.noLevel')
      }
      return levelList.value?.find(({ level }) => curLevel === level)?.name
    })

    const memberLevelExpiredDate = computed(() => {
      const curLevel = member.value?.level
      if (curLevel === 0) return '-'
      const expiredAt = member.value?.levelExpiredAt
      return expiredAt ? dayjs(expiredAt).format('YYYY/MM/DD') : t('member.memberInfoBlock.permanent')
    })

    const memberAvatar = computed(() => {
      const avatar = member.value?.UserInfo?.Image
      return avatar
        ? imgSrc(800, avatar)
        : require('@/assets/icon/img-placeholder.png')
    })

    const memberAuthLineDisplayName = computed(() => {
      const name = memberTopInfo?.value?.memberAuthLine?.displayName
      // 當沒有名稱或 memberAuthLine 為 null 時，顯示 '-'
      return name || '-'
    })

    /**
     * 來源分店（CNCY-10069）：讀取後端回傳的 UserInfo.sourceShopId / sourceShopName。
     * - 有 sourceShopName → 顯示分店名稱
     * - 有 sourceShopId 但無對應 Shop（查無）→ 顯示「未知分店」（SD §13 SR-8 fallback）
     * - 皆無（既有會員未回填）→ 顯示「未記錄」（O3 決策）
     * 不再 fallback 到 member.Shop.name（當前所屬分店），避免與「來源分店」語意混淆。
     */
    const sourceShop = computed(() => {
      const sourceShopName = get(member.value, 'sourceShopName')
      if (sourceShopName) return sourceShopName
      const sourceShopId = get(member.value, 'sourceShopId')
      if (sourceShopId) return '未知分店'
      return '未記錄'
    })

    // 聚合要顯示在畫面的會員資訊，方便 template 使用與測試
    const displayMemberInfo = computed(() => {
      const userInfo = member.value?.UserInfo || {}
      return {
        name: userInfo.name || '-',
        avatar: memberAvatar.value,
        level: memberLevel.value,
        levelExpiredDate: memberLevelExpiredDate.value,
        phone: userInfo.phone || '-',
        birthday: userInfo.birthday ? formatDate(userInfo.birthday, 'YYYY/MM/DD') : '-',
        totalPaymentPriceSum: totalPaymentPriceSum?.value ?? 0,
        lineDisplayName: memberAuthLineDisplayName.value,
        sourceShop: sourceShop.value,
      }
    })

    // ========================================
    // Methods
    // ========================================
    const handleUpdateCustomTags = (tags) => {
      if (updateMemberTags) {
        updateMemberTags(tags)
      }
    }

    const syncCyjPosMember = async () => {
      if (!memberId.value) return
      syncCyjPosLoading.value = true
      try {
        const [, err] = await SyncCyjPosMemberImmediately({
          shopId: shopId.value,
          memberId: memberId.value,
        })
        if (err) {
          window.$message.error(err)
          return
        }
        window.$message.success(t('member.memberProfile.syncSuccess'))
        await fetchMemberTopInfo()
      } finally {
        syncCyjPosLoading.value = false
      }
    }

    // ========================================
    // Lifecycle
    // ========================================
    onMounted(() => {
      const openInviteRecord = get(route.query, 'openInviteRecord')
      if (openInviteRecord) {
        show.inviteRecord = true
      }
      // externalWalletConfig 已從 GetMemberTopInfo 取得，無需額外呼叫
    })

    return {
      // Data
      show,
      dayjs,

      // Injected (需要在模板中使用的)
      member,
      memberTopInfo,
      tags,
      customTags,
      reservationStatistic,
      totalPaymentPriceSum,
      rebateWalletBalance,
      pointWalletBalance,
      pospalWalletBalance,
      externalPointWalletBalance,
      usePospal,
      useExternalPoint,
      levelList,

      // Computed
      memberId,
      showControl,
      cyjPosCsid,
      syncCyjPosLoading,
      memberLevel,
      memberLevelExpiredDate,
      memberAvatar,
      memberAuthLineDisplayName,
      displayMemberInfo,

      // Methods
      handleUpdateCustomTags,
      syncCyjPosMember,
    }
  },
})
</script>

<style lang="scss" scoped>
.member-info-block {
  @apply p-[15px] rounded-sub bg-white;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.15);
}

.user-avatar {
  @apply w-[135px] h-[135px] rounded-[10px] flex-shrink-0;
}

.user-container {
  @apply flex flex-col flex-1 px-5 gap-2;
}

.user-statistic {
  @apply flex gap-[8px];
  @apply items-center;
}

.user-name {
  @apply text-[14px] leading-[20px];
}

.info-label {
  @apply text-gray-80 text-[12px] leading-[27px];
}
</style>
